import React, { useState, useEffect } from 'react';
import { MessageSquare, Save, Send, Loader2, CheckCircle, XCircle, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


export default function EmpresaWhatsAppConfigPage() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [newToken, setNewToken] = useState('');

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => {
        fetch(`${API}/enterprise/whatsapp-config`, { headers })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                setConfig(data || { enabled: false, phone_number_id: '', business_account_id: '', recipient_number: '', status: 'not_configured' });
                setLoading(false);
            }).catch(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                enabled: config.enabled,
                phone_number_id: config.phone_number_id,
                business_account_id: config.business_account_id,
                recipient_number: config.recipient_number,
            };
            if (newToken) payload.access_token = newToken;
            const r = await fetch(`${API}/enterprise/whatsapp-config`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (r.ok) {
                const data = await r.json();
                toast.success('Configuracao salva!');
                setConfig({ ...config, status: data.status });
                setNewToken('');
            } else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexao'); }
        setSaving(false);
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const r = await fetch(`${API}/enterprise/whatsapp-config/test`, { method: 'POST', headers });
            const data = await r.json();
            if (r.ok) toast.success(data.message);
            else toast.error(data.detail || 'Erro no teste');
        } catch { toast.error('Erro de conexao'); }
        setTesting(false);
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const isConfigured = config?.status === 'configured';

    return (
        <div className="space-y-5" data-testid="whatsapp-config-page">
            {/* Header */}
            <div>
                <h1 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>WhatsApp Business API</h1>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>Configure o envio de notificacoes via WhatsApp para sua empresa</p>
            </div>

            {/* Status */}
            <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'var(--surface)', border: `1px solid ${isConfigured ? 'rgba(16,185,129,0.2)' : 'var(--border)'}` }}>
                {isConfigured ? <CheckCircle size={18} style={{ color: '#10B981' }} /> : <XCircle size={18} style={{ color: 'var(--muted-2)' }} />}
                <div>
                    <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{isConfigured ? 'WhatsApp configurado' : 'WhatsApp nao configurado'}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{isConfigured ? 'Pronto para enviar notificacoes' : 'Preencha as credenciais abaixo'}</p>
                </div>
                <label className="flex items-center gap-2 ml-auto cursor-pointer">
                    <input type="checkbox" checked={config?.enabled || false} onChange={e => setConfig({ ...config, enabled: e.target.checked })} className="accent-[var(--sv-accent)]" />
                    <span className="text-xs" style={{ color: 'var(--text)' }}>{config?.enabled ? 'Ativo' : 'Inativo'}</span>
                </label>
            </div>

            {/* Configuration Form */}
            <div className="rounded-xl p-4 space-y-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 mb-2">
                    <Shield size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                    <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Credenciais Meta Business</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Phone Number ID *</label>
                        <input type="text" value={config?.phone_number_id || ''} onChange={e => setConfig({ ...config, phone_number_id: e.target.value })} placeholder="Ex: 123456789012345" className="w-full rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="wa-phone-number-id" />
                        <p className="text-[9px] mt-0.5" style={{ color: 'var(--muted-2)' }}>Meta Developer Dashboard &gt; WhatsApp &gt; API Setup</p>
                    </div>
                    <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Business Account ID</label>
                        <input type="text" value={config?.business_account_id || ''} onChange={e => setConfig({ ...config, business_account_id: e.target.value })} placeholder="Ex: 987654321098765" className="w-full rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="wa-business-account-id" />
                    </div>
                    <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Access Token *</label>
                        <div className="relative">
                            <input type={showToken ? 'text' : 'password'} value={newToken} onChange={e => setNewToken(e.target.value)} placeholder={config?.access_token_masked || 'Cole o token aqui'} className="w-full rounded-lg px-3 py-2 pr-10 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="wa-access-token" />
                            <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }}>
                                {showToken ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                        </div>
                        {config?.access_token_masked && <p className="text-[9px] mt-0.5" style={{ color: 'var(--muted-2)' }}>Token atual: {config.access_token_masked}</p>}
                    </div>
                    <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Numero de destino *</label>
                        <input type="text" value={config?.recipient_number || ''} onChange={e => setConfig({ ...config, recipient_number: e.target.value })} placeholder="Ex: 5511999998888" className="w-full rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="wa-recipient-number" />
                        <p className="text-[9px] mt-0.5" style={{ color: 'var(--muted-2)' }}>Formato: codigo do pais + DDD + numero (sem +)</p>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                    <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="save-whatsapp-config">
                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar
                    </button>
                    {isConfigured && (
                        <button onClick={handleTest} disabled={testing} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium" style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)' }} data-testid="test-whatsapp-btn">
                            {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Enviar Teste
                        </button>
                    )}
                </div>
            </div>

            {/* Info */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.1)' }}>
                <p className="text-xs font-medium mb-1.5" style={{ color: '#3B82F6' }}>Como obter as credenciais?</p>
                <ol className="text-[10px] space-y-1" style={{ color: 'var(--muted)' }}>
                    <li>1. Acesse developers.facebook.com</li>
                    <li>2. Crie ou acesse seu app &gt; Adicione o produto WhatsApp</li>
                    <li>3. Em "API Setup", copie o Phone Number ID e gere um Access Token permanente</li>
                    <li>4. O Business Account ID esta nas configuracoes da sua conta Business</li>
                    <li>5. O numero de destino deve estar registrado no WhatsApp</li>
                </ol>
            </div>
        </div>
    );
}
